import {
  CatchmentMark,
  CatchmentWordmark,
  ClaudeMark,
  SocialProWordmark,
  SocialProMark
} from "./logoMarks";

/**
 * Brand marks. Deliberately not part of `Icon`: these are multi-colour, take
 * gradient stops rather than a tone, and are not square — the wordmark is
 * 125x29. An `Icon` interface that promised `size` and `tone` here could not
 * honour either.
 *
 * `catchment` and `catchment-wordmark` are the current brand, introduced with
 * the side-nav redesign and used there. The SocialPro `mark` / `wordmark` pair
 * stays for the surfaces that still draw it (error page, assistant, progress
 * ring) until each is moved over deliberately.
 */
export type LogoVariant =
  | "claude"
  | "mark"
  | "wordmark"
  | "catchment"
  | "catchment-wordmark";

const DEFAULT_WIDTH: Record<LogoVariant, number> = {
  catchment: 32,
  "catchment-wordmark": 191,
  claude: 20,
  mark: 20,
  wordmark: 24
};

const MARKS: Record<Exclude<LogoVariant, "claude">, typeof SocialProMark> = {
  catchment: CatchmentMark,
  "catchment-wordmark": CatchmentWordmark,
  mark: SocialProMark,
  wordmark: SocialProWordmark
};

export interface LogoProps {
  variant?: LogoVariant;
  /** Rendered box width in px. */
  width?: number;
  /** Rendered box height in px. Defaults to `width` — see the note in `Logo`. */
  height?: number;
  /** Gradient start. Ignored by `claude`, whose mark is fixed brand colours. */
  startColor?: string;
  /** Gradient end. Ignored by `claude`. */
  endColor?: string;
  /**
   * Accessible name. Omit when the logo sits beside the product name or inside
   * an already-labelled link — the default is decorative.
   */
  label?: string;
}

const Logo = ({
  endColor = "#F0F0F0",
  height,
  label,
  startColor = "#FFFFFF",
  variant = "mark",
  width
}: LogoProps) => {
  const w = width ?? DEFAULT_WIDTH[variant];
  // TODO(icon-module): height defaults to width, which draws every mark into a
  // square box. Only `claude` is actually square: the mark is 38x29 and the
  // wordmark 125x29, so the SVG's own preserveAspectRatio letterboxes them and
  // the box reserves space the mark never fills — `<Logo variant="wordmark"
  // width={90} />` reserves 90x90 to draw 90x21. Deriving height from the
  // viewBox is the fix, but it moves layout wherever a logo sits in a flex row,
  // so it is deferred to its own commit with a visual pass. Preserved here so
  // the migration stays behaviour-preserving.
  const h = height ?? w;

  if (variant === "claude") {
    return <ClaudeMark height={h} label={label} width={w} />;
  }

  const Mark = MARKS[variant];

  return (
    <Mark
      endColor={endColor}
      height={h}
      label={label}
      startColor={startColor}
      width={w}
    />
  );
};

export default Logo;
